import { createFileRoute, ErrorComponent, type ErrorComponentProps } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { portalProjectQueryOptions, PortalError } from '@/api/client'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ContactPanel, OrderTracker } from '@/components/portal/order-tracker'
import { SITE_NAME, trackerTitle } from '@/lib/tracker'

export const Route = createFileRoute('/p/$token')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(portalProjectQueryOptions(params.token)),
  errorComponent: PortalErrorView,
  pendingComponent: PortalLoading,
  component: PortalView,
})

function PortalView() {
  const { token } = Route.useParams()
  const { data } = useSuspenseQuery(portalProjectQueryOptions(token))
  const several = data.orders.length > 1
  const title = trackerTitle(data)
  useEffect(() => {
    document.title = title
    return () => {
      document.title = SITE_NAME
    }
  }, [title])
  return (
    <div className="selection:bg-usa-blue/15 mx-auto w-full max-w-5xl px-4 pt-8 pb-16 sm:px-6 sm:pt-12">
      {several ? (
        <h1 className="mb-8 text-[2rem] leading-tight font-semibold tracking-tight">Your orders</h1>
      ) : null}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-12">
        <div className="space-y-14">
          {data.orders.map((order, index) => (
            <OrderTracker
              key={order.order_number ?? index}
              order={order}
              token={token}
              headingLevel={several ? 2 : 1}
            />
          ))}
          {data.orders.length === 0 ? (
            <h1 className="text-[2rem] leading-tight font-semibold tracking-tight">
              Order received
            </h1>
          ) : null}
        </div>
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <ContactPanel contact={data.contact} />
        </aside>
      </div>
    </div>
  )
}

function PortalLoading() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 pt-8 pb-16 sm:px-6 sm:pt-12">
      <div className="bg-muted h-10 w-72 max-w-full animate-pulse rounded-lg" />
      <div className="bg-muted mt-3 h-5 w-56 animate-pulse rounded" />
      <div className="bg-muted mt-10 h-56 animate-pulse rounded-2xl lg:max-w-[calc(100%-21rem)]" />
    </div>
  )
}

function PortalErrorView({ error }: ErrorComponentProps) {
  if (error instanceof PortalError) {
    if (error.status === 404 || error.status === 410) {
      return (
        <div className="mx-auto w-full max-w-3xl px-6 py-16">
          <Card className="text-center">
            <CardHeader>
              <CardTitle>
                {error.status === 410 ? 'This order tracker link has expired' : 'Order tracker not found'}
              </CardTitle>
              <CardDescription className="mt-2 text-base">
                Reply to any email about your order and we&apos;ll send you a new link.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      )
    }
  }
  return <ErrorComponent error={error} />
}
